// apps/admin/src/api/queries/financeKeys.ts
//
// Phase 8.2 Wave 1 Task 04 — Hierarchical TanStack Query key factory for
// the admin Finance surface (Billing tabs / Credits / Payouts /
// Transactions). Centralising keys here enforces invariant cache slice
// boundaries — every downstream component imports `financeKeys.*` and
// the invalidation graph stays grep-able from a single source.
//
// Per CONTEXT.md
//   • Billing has 3 tabs (Topup Reviews / Credits / Gateways);
//                each tab is a distinct cache slice under
//                ['finance','billing',<tab>].
//   • Payouts state-machine changes (approve / reject
//                mark-transferred) invalidate ['finance','payouts'].
//   • Transactions feed the 4 summary cards; filter changes
//                produce distinct cache slices so paginating one filter
//                set never blows the cache for another.
//   • Credits versioned schema (per-merchant + cross-merchant
//                slices stay independent so editing one doesn't refetch
//                the other unless explicitly invalidated).
//   • Topup-review filter dimension `verification_status`
//                lives inside the `filters` object so it participates in
//                the cache-slice fingerprint.
//
// Key convention (hierarchical for `invalidateQueries({ queryKey: [...] })`):
//
//   ['finance']                                       — wipe everything
//   ['finance','billing']                             — wipe billing-only
//   ['finance','billing','topup-reviews']             — wipe all topup tabs
//   ['finance','billing','topup-reviews','list',F]    — one filter slice
//   ['finance','billing','topup-reviews','detail',id] — one row drawer
//
//   ['finance','credits']                             — cross-merchant credit list
//   ['finance','credits','list',F]
//
//   ['finance','payouts']                             — payouts root
//   ['finance','payouts','list',F]
//   ['finance','payouts','detail',id]
//
//   ['finance','transactions']                        — transactions root
//   ['finance','transactions','list',F]
//
// Pure factory — no hooks, no React, no axios. Safe to import from any
// layer (queries, mutations, tests, devtools).

import type {
  AdminInvoiceListFilters,
  FinanceCreditListFilters,
  PayoutListFilters,
  TopupReviewListFilters,
  TransactionListFilters,
} from '../../types/finance.js';

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

/**
 * Hierarchical query-key factory. Each level returns a `readonly` tuple so
 * TanStack Query's structural-equality cache key compare works without any
 * runtime serialisation tricks.
 *
 * Usage:
 *   useQuery({ queryKey: financeKeys.topupReviews.list(filters), ... });
 *   qc.invalidateQueries({ queryKey: financeKeys.topupReviews.all });
 *   qc.invalidateQueries({ queryKey: financeKeys.all });
 */
export const financeKeys = {
  /** Root — invalidates EVERY finance cache slice. */
  all: ['finance'] as const,

  billing: {
    all: ['finance', 'billing'] as const,
  },

  topupReviews: {
    all: ['finance', 'billing', 'topup-reviews'] as const,
    lists: () => ['finance', 'billing', 'topup-reviews', 'list'] as const,
    list: (filters: TopupReviewListFilters) =>
      ['finance', 'billing', 'topup-reviews', 'list', filters] as const,
    details: () => ['finance', 'billing', 'topup-reviews', 'detail'] as const,
    detail: (topupId: string) =>
      ['finance', 'billing', 'topup-reviews', 'detail', topupId] as const,
  },

  credits: {
    all: ['finance', 'credits'] as const,
    lists: () => ['finance', 'credits', 'list'] as const,
    list: (filters: FinanceCreditListFilters) =>
      ['finance', 'credits', 'list', filters] as const,
    details: () => ['finance', 'credits', 'detail'] as const,
    detail: (creditId: string) =>
      ['finance', 'credits', 'detail', creditId] as const,
  },

  payouts: {
    all: ['finance', 'payouts'] as const,
    lists: () => ['finance', 'payouts', 'list'] as const,
    list: (filters: PayoutListFilters) =>
      ['finance', 'payouts', 'list', filters] as const,
    details: () => ['finance', 'payouts', 'detail'] as const,
    detail: (payoutId: string) =>
      ['finance', 'payouts', 'detail', payoutId] as const,
  },

  transactions: {
    all: ['finance', 'transactions'] as const,
    lists: () => ['finance', 'transactions', 'list'] as const,
    list: (filters: TransactionListFilters) =>
      ['finance', 'transactions', 'list', filters] as const,
    details: () => ['finance', 'transactions', 'detail'] as const,
    detail: (txnId: string) =>
      ['finance', 'transactions', 'detail', txnId] as const,
  },

  /**
   * Admin invoices — cross-merchant list + detail slices (Wave 3
   * Task 11 / Task 12). Bulk resend-email mutation invalidates `all`
   * so the list re-fetches the updated overdue_count / total_outstanding.
   */
  invoices: {
    all: ['finance', 'invoices'] as const,
    lists: () => ['finance', 'invoices', 'list'] as const,
    list: (filters: AdminInvoiceListFilters) =>
      ['finance', 'invoices', 'list', filters] as const,
    details: () => ['finance', 'invoices', 'detail'] as const,
    detail: (invoiceId: string) =>
      ['finance', 'invoices', 'detail', invoiceId] as const,
  },
} as const;

export type FinanceQueryKeys = typeof financeKeys;
